import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../common/model/dto/order/order';
import {
  getDeliveryStatusLabel,
  getDeliveryStatusTone,
  getOrderDeliveryState,
} from '../../../common/utils/services/order-delivery-utils';
import { OrderDataUtils } from '../../../common/utils/services/order-data-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvStatusChip from '../atoms/OvStatusChip';

interface OvOrderDeliveryStatusProps {
  order: Order;
  showDetail?: boolean;
}

const OvOrderDeliveryStatus: FC<OvOrderDeliveryStatusProps> = ({
  order,
  showDetail = false,
}) => {
  const { t } = useTranslation();
  const deliveryState = getOrderDeliveryState(order);

  switch (deliveryState.kind) {
    case 'unavailable':
      return <Calm>{t('orders.delivery.notAvailable')}</Calm>;

    case 'noData':
      return <Calm>{t('orders.delivery.noDataShort')}</Calm>;

    case 'delivered':
      return (
        <>
          <OvStatusChip label={t('orders.delivery.delivered')} tone="success" />
          {showDetail && (
            <Detail>
              {OrderDataUtils.formatDateTime(
                deliveryState.deliveredAt,
                'DD MMM YYYY'
              )}
            </Detail>
          )}
        </>
      );

    case 'overdue':
      return (
        <>
          <OvStatusChip
            label={t('orders.delivery.overdueDelivery')}
            tone="caution"
          />
          {showDetail && (
            <Detail>
              {t('orders.delivery.shippedDaysAgo', {
                count: deliveryState.daysSinceShipped,
              })}
            </Detail>
          )}
        </>
      );

    case 'notConfirmed':
      return (
        <OvStatusChip
          label={t('orders.delivery.notConfirmed')}
          tone="caution"
        />
      );

    case 'inTransit':
      return (
        <OvStatusChip
          label={
            deliveryState.deliveryStatus
              ? getDeliveryStatusLabel(deliveryState.deliveryStatus, t)
              : t('orders.delivery.shipped')
          }
          tone={getDeliveryStatusTone(deliveryState.deliveryStatus)}
        />
      );

    default: {
      const unhandledState: never = deliveryState;

      return unhandledState;
    }
  }
};

export default OvOrderDeliveryStatus;

const Calm = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const Detail = styled.span`
  display: block;
  margin-top: 0.25rem;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;
